import { isAdminRole } from '@/helpers/roles';
import { useAppSelector } from '@/store/hooks';
import { selectReportingV2Filters, selectReportingV2Freshness } from '@/store/slices/reportingV2/reportingV2Slice';
import { FileDownloadOutlined, KeyboardArrowDownRounded, PrintOutlined } from '@mui/icons-material';
import { Box, Button, Divider, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { freshnessMessage } from '../lib/freshnessMessage';
import { COLORS } from '../theme';
import ExportDialog from './ExportDialog';
import SavedReportsMenu from './SavedReportsMenu';

const outlineSx = {
  textTransform: 'none',
  color: COLORS.text,
  borderColor: COLORS.border,
  bgcolor: '#fff',
  borderRadius: '8px',
  fontSize: '0.8rem',
  '&:hover': { borderColor: COLORS.muted, bgcolor: '#fff' },
} as const;

const ReportingV2Header = () => {
  const [exportMenu, setExportMenu] = useState<HTMLElement | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const freshness = useAppSelector(selectReportingV2Freshness);
  const filters = useAppSelector(selectReportingV2Filters);
  const role = useAppSelector(state => state.me.role);
  // #4586: say only what is wrong, and only when something is. The wording
  // rules live in freshnessMessage so they can be tested without the header.
  // flank#5130 removed the "Data through …" line, its info popup, and the
  // "last 5 days may still change" note; the admin-only stale-platform
  // warning stays pending Martin's call on whether it should go too.
  const { warning } = freshnessMessage(freshness);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box>
        {/* Own export section (flank#4602) — separate from the date-range line
            below, which must keep printing even when this is unchecked (it's
            the only thing that says what window a printed page covers). */}
        <Box data-export-section='performanceOverview'>
          {/* h6, not h4 — this now sits inside the compact sticky header (#4626)
              rather than a full-height block of its own. */}
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: COLORS.text, lineHeight: 1.2 }}
          >
            Performance Overview
          </Typography>
          {/* flank#5130: Joe asked for the "Data through …" line, its info
              popup, and the "last 5 days may still change" note gone — the
              line read as stale even when accurate. The stale-platform
              warning (#4464) is unrelated and stays, admin-only, for now. */}
          {isAdminRole(role) && warning && (
            <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography variant='body2' sx={{ color: COLORS.red, fontWeight: 600 }}>
                {warning}
              </Typography>
            </Box>
          )}
        </Box>
        {/*
          Print-only: the filter bar is hidden in print, so without this the
          printed report states no date range at all and can't be interpreted
          after the fact.
        */}
        <Typography variant='body2' sx={{ color: COLORS.muted, display: 'none', displayPrint: 'block' }}>
          {`Reporting window: ${filters.dateStart} to ${filters.dateEnd}`}
        </Typography>
      </Box>
      {/* The print action must not appear in its own output. */}
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' sx={{ displayPrint: 'none' }}>
        <SavedReportsMenu />
        <Button
          variant='outlined'
          startIcon={<FileDownloadOutlined sx={{ fontSize: 15 }} />}
          endIcon={<KeyboardArrowDownRounded sx={{ fontSize: 18 }} />}
          onClick={e => setExportMenu(e.currentTarget)}
          sx={outlineSx}
        >
          Export
        </Button>
        <Menu
          anchorEl={exportMenu}
          open={Boolean(exportMenu)}
          onClose={() => setExportMenu(null)}
          slotProps={{ paper: { sx: { mt: 0.5, minWidth: 240, borderRadius: '10px' } } }}
        >
          <MenuItem
            dense
            onClick={() => {
              setExportMenu(null);
              setExportOpen(true);
            }}
          >
            Export options (CSV, Excel, PDF)…
          </MenuItem>
          <MenuItem
            dense
            onClick={() => {
              setExportMenu(null);
              window.print();
            }}
          >
            Save as PDF (print)
          </MenuItem>
          <Divider />
          {/* Shown but inert: the reference offers these and hiding them would
              imply they're gone rather than not built. Disabled says which. */}
          <MenuItem dense disabled>
            Build branded report… (coming soon)
          </MenuItem>
          <MenuItem dense disabled>
            Schedule recurring email… (coming soon)
          </MenuItem>
        </Menu>
        <Button
          variant='outlined'
          startIcon={<PrintOutlined sx={{ fontSize: 15 }} />}
          onClick={() => window.print()}
          sx={outlineSx}
        >
          Print
        </Button>
      </Stack>
      {exportOpen && <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />}
    </Box>
  );
};

export default ReportingV2Header;
